import { useMemo } from "react";
import { dayjs } from "utils";
import { ColumnDef } from "@tanstack/react-table";
import log from "logbeacon";
import { Stock } from "../features/api/openData";
import { getBSData, getCFData, getICData } from "../utils/stock";
import Translations from "@translate/hooks/useStockFinacialsHandle";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

// type Financial = any;

export type IUseStockDetailsHandleProps = {
  stockData?: MaybeNullable<Stock>;
  tab: "ic" | "bs" | "cf";
  isPC?: boolean;
};

export const useStockFinacialsHandle = (props: IUseStockDetailsHandleProps) => {
  const { stockData, tab, isPC = true } = props;

  const columns = useMemo(() => {
    const financials = stockData?.financials?.items;
    if (!financials?.length) {
      return [];
    }
    const columns: ColumnDef<Record<string, Maybe<string | number>>>[] = [
      {
        accessorKey: "breakdown",
        id: "breakdown",
        header: "Breakdown",
        meta: {
          width: isPC ? 200 : 160,
          pinned: "left" as const,
          headerClassName: "px-3 py-2 body_bold_s",
          cellClassName: "px-3 py-[6px] h-[52px]",
        },
      },
    ];
    financials.forEach((financial) => {
      columns.push({
        // TODO 根据某个值判断是不是TTM
        accessorKey: financial.freq === "ttm" ? "ttm" : financial.period,
        id: financial.freq === "ttm" ? "ttm" : financial.period,
        header:
          financial.freq === "ttm"
            ? I18n.t("TTM")
            : dayjs(financial.period).format("MM/DD/YYYY"),
        meta: {
          width: 120,
          headerClassName: "px-3 py-2 body_bold_s",
          cellClassName: "px-3 py-[6px] h-[52px] w-[200px]",
        },
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return value || "--";
        },
      });
    });
    if (tab === "bs") {
      return columns.filter(
        (item) =>
          (item as unknown as { accessorKey: string }).accessorKey !== "ttm",
      );
    }
    return columns;
  }, [stockData?.financials, tab, isPC]);

  const dataMap = useMemo(() => {
    return {
      bs: getBSData,
      ic: getICData,
      cf: getCFData,
    };
  }, []);

  const data = useMemo(() => {
    const financials = stockData?.financials?.items;
    if (!financials?.length) {
      return [];
    }
    const getData = dataMap[tab];
    if (!getData) {
      return [];
    }
    const data = getData();
    const result = data.map((item) => {
      return {
        breakdown: item.breakdown,
        ...item.getValue(financials),
      };
    });
    // 过滤掉无效数据
    const newResult = result.filter((item) => {
      const values = Object.values(item).filter(
        (value) => typeof value !== "undefined",
      );
      return values.length > 1;
    });
    // 无效数据为
    const invalidData = result.filter((item) => {
      const values = Object.values(item).filter(
        (value) => typeof value !== "undefined",
      );
      return values.length < 2;
    });
    log.info(
      "[useStockFinacialsHandle] invalidData 不展示的无效数据为",
      invalidData,
    );
    return newResult;
  }, [stockData?.financials, tab, dataMap]);

  return {
    themeIds: stockData?.theme_ids,
    columns,
    data,
  };
};
